/**
 * FAQ keywords (portal 4.8.0, Part A). A Knowledge agent's AI generated FAQs are retrieved and
 * written from a fixed set of keywords for its use case, so every page gets the same questions.
 * They live in `ragConfig.articleRelatedQuestions` as `audience`, `keywords` and `keywordsEdited`.
 * The default keyword lists and the legacy accounts come from the API
 * (`GET /campaign/FAQKeywordConfig`), which mirrors the AI layer's own lists.
 */

export const MAX_FAQ_KEYWORDS = 8;
export const MAX_FAQ_KEYWORD_LENGTH = 40;

// The use cases an organiser chooses from. `event` is not one of them: it's the AI layer's
// temporary default, kept by agents set up before use cases.
export const FAQ_USE_CASES = [
  { audience: 'registrant', label: 'Registrants', plural: 'registrants' },
  { audience: 'sponsor', label: 'Sponsors', plural: 'sponsors' },
  { audience: 'awards', label: 'Awards', plural: 'award entrants' },
];

export const FAQ_EVENT_DEFAULT = 'event';

export const getFAQUseCase = audience =>
  FAQ_USE_CASES.find(useCase => useCase.audience === audience) ?? null;

export const getDefaultFAQKeywords = (config, audience) =>
  config?.useCases?.find(useCase => useCase.audience === audience)?.keywords ?? [];

export const sameFAQKeywords = (a = [], b = []) =>
  a.length === b.length && a.every((keyword, index) => keyword === b[index]);

// Trims and collapses spaces, and cuts to the longest a keyword can be.
export const normaliseFAQKeyword = value =>
  String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, MAX_FAQ_KEYWORD_LENGTH);

const isAIGenerated = faq => faq?.isActive && faq?.relatedQuestionType === 'AIGenerated';

/**
 * An agent saved before use cases: opened for editing with AI generated FAQs and no use case. The
 * AI runs it on the Event default until someone chooses one.
 */
export const isFAQEventDefault = (faq, creationType) =>
  isAIGenerated(faq) &&
  !getFAQUseCase(faq?.audience) &&
  (faq?.audience === FAQ_EVENT_DEFAULT ||
    (!faq?.audience && (creationType === 'edit' || creationType === 'agentEdit')));

/**
 * What stops agent setup from going on past Design, if anything. Legacy accounts, static questions
 * and a keyword config that couldn't load aren't checked.
 */
export const getFAQKeywordProblem = (faq, config) => {
  if (!isAIGenerated(faq) || !config || config.isLegacy) return '';
  if (!getFAQUseCase(faq.audience)) {
    return 'Choose who the FAQ questions are for in the FAQs settings.';
  }
  if (!faq.keywords?.length) {
    return 'Add at least one FAQ keyword, or use the defaults, in the FAQs settings.';
  }
  return '';
};

/**
 * The use case badge on the FAQs row ("Registrants", "Sponsors", "Awards"). Nothing for static
 * questions, legacy accounts, the Event default or no use case yet.
 */
export const getFAQUseCaseLabel = (faq, { config, creationType } = {}) => {
  if (!isAIGenerated(faq) || config?.isLegacy || isFAQEventDefault(faq, creationType)) return '';
  return getFAQUseCase(faq.audience)?.label ?? '';
};

/**
 * One line for the FAQs row (Tools & add-ons) and the Publish summary. `useCaseInBadge` leaves the
 * use case out, for the row, where a badge beside the title names it.
 */
export const getFAQSummary = (faq, { config, creationType, useCaseInBadge = false } = {}) => {
  if (!faq?.isActive) return 'Off';
  if (faq.relatedQuestionType !== 'AIGenerated') {
    return `Static questions · ${faq.questions?.length ?? 0}`;
  }
  if (config?.isLegacy) {
    return `AI generated from each page, ${config.legacyClientName}'s legacy workflow`;
  }
  if (isFAQEventDefault(faq, creationType)) {
    return 'AI generated with the Event default · choose a use case';
  }
  const useCase = getFAQUseCase(faq.audience);
  if (!useCase) return 'AI generated · choose who the questions are for';
  const keywords = faq.keywords?.join(', ');
  if (useCaseInBadge) {
    return keywords ? `AI generated from ${keywords}` : 'AI generated · add keywords';
  }
  if (!keywords) return `AI generated for ${useCase.plural} · add keywords`;
  return `AI generated for ${useCase.plural}, from ${keywords}`;
};

// Set questions for Bridged's default keywords. Production's questions are written by the AI from
// what the keywords retrieve; these only show the organiser what to expect in the live preview.
const SAMPLE_QUESTIONS = {
  register: name => `How do I register for ${name}?`,
  tickets: name => `What tickets are there for ${name}?`,
  pricing: name => `How much does it cost to attend ${name}?`,
  agenda: name => `Where can I see the full agenda for ${name}?`,
  sessions: () => 'Which sessions should I not miss?',
  speakers: name => `Who's speaking at ${name}?`,
  venue: name => `Where is ${name}, and how do I get there?`,
  networking: () => 'What networking is there for attendees?',
  'sponsorship packages': () => "What sponsorship packages are there, and what's in each one?",
  prospectus: () => 'Where can I get the sponsorship prospectus?',
  exhibitor: name => `How do I exhibit at ${name}?`,
  'floor plan': () => 'Where can I see the floor plan and the stands that are left?',
  audience: name => `Who comes to ${name}?`,
  'attendee demographics': () => 'What job titles and sectors do the attendees come from?',
  'lead generation': () => 'How can sponsors collect leads at the event?',
  'media pack': () => 'Where can I get the media pack?',
  'award categories': () => 'Which award categories are open this year?',
  eligibility: () => 'Who is eligible to enter the awards?',
  'entry deadline': () => 'When is the deadline to enter the awards?',
  'entry fee': () => 'How much does it cost to enter the awards?',
  'judging criteria': () => 'How are entries judged?',
  shortlist: () => 'When is the shortlist announced?',
  'previous winners': () => 'Who won the awards last year?',
  ceremony: () => 'When and where is the awards ceremony?',
};

/** Sample questions for the live preview: one per keyword. */
export const buildFAQSampleQuestions = (keywords = [], name) => {
  const subject = String(name || '').trim() || 'the event';
  return keywords.map(keyword => {
    const sample = SAMPLE_QUESTIONS[String(keyword).toLowerCase()];
    return sample ? sample(subject) : `What should I know about ${keyword} at ${subject}?`;
  });
};
